import { useState, useCallback, useMemo } from 'react'
import { useAppSelector } from '@/store/store-hooks'
import { selectIsUserFilesOperationActive } from '@/user_files/batching/store/selectors'
import { kNoSelectionAncestry, pruneCoveredSelection } from '@/user_files/batching/prune-covered-selection'
import type { FileSelectionPrecondition, FolderSelectionPrecondition } from '@/user_files/schemas/preconditions'
import { buildFileSelectionPrecondition, buildFolderSelectionPrecondition } from '@/user_files/utils/preconditions'
import type { FileForSelection, FolderForSelection } from './useDriveSelection'
import { isLocalStorageItem } from '@/user_files/external-storage/utils/external-storage-items'
import { isWaitingForOcrQuota } from '@/user_files/utils/processing-status'

type UseMoveDialogParams = {
  files: FileForSelection[]
  folders: FolderForSelection[]
  /** The ID of the current folder. */
  currentFolderId: string | null
  /** An array containing the IDs of all selected selectable files. */
  selectedSelectableFileIds: string[]
  /** An array containing the IDs of all selected folders. */
  selectedFolderIds: string[]
  /**
   * Ancestor folder ids per visible row id, present only when the rows are search results.
   * Used to drop items whose containing folder is moving too — see `pruneCoveredSelection`.
   */
  ancestorFolderIdsById?: ReadonlyMap<string, readonly string[]>
}

type OpenMoveItemsArgs = {
  /** An array containing the IDs of all files to move. */
  fileIds?: string[]
  /** An array containing the IDs of all folders to move. */
  folderIds?: string[]
}

type UseMoveDialogMoveDialog = {
  /** Whether the move-destination UI is open. */
  open: boolean
  /** File IDs to move (snapshot taken when the dialog opens). */
  fileIds: string[]
  /** Folder IDs to move (snapshot taken when the dialog opens). */
  folderIds: string[]
  /**
   * What the user ticked, which the dialog titles itself from. Larger than the id lists above
   * whenever the selection covers an item inside a folder it also covers, because that item is
   * pruned out of the move but is still one of the things the user picked — and still one of the
   * things that ends up in the destination. Keeps the title and the contextual toolbar on the same
   * number (FE-594 / BB-04).
   */
  selectedFileCount: number
  selectedFolderCount: number
  filePreconditions: FileSelectionPrecondition[]
  folderPreconditions: FolderSelectionPrecondition[]
  /**
   * The folder every item being moved currently lives in, or `null` when they span more than one —
   * which only happens for a selection made in search results. It is what makes a destination a
   * real move, and is not the folder on screen: a search selection can name items from anywhere.
   */
  sourceFolderId: string | null
}

type UseMoveDialogReturn = {
  /** Visibility and IDs to move; IDs are fixed at open time so confirm uses a stable snapshot. */
  moveDialog: UseMoveDialogMoveDialog
  /** Opens the dialog using the current drive selection. */
  openMoveForSelected: () => void
  /** Move remains local-only, and excludes files waiting for OCR quota, even though both can be selected for Delete. */
  moveSelectedDisabled: boolean
  /** Opens the dialog for explicit file and/or folder IDs. */
  openMoveItems: (args: OpenMoveItemsArgs) => void
  /** Closes the dialog and clears captured IDs and errors. */
  closeMoveDialog: () => void
  /** True while any user-files batched operation is in progress. */
  isSubmitting: boolean
}

/**
 * The one folder every item being moved sits in, or `null` when they don't share one.
 *
 * The parents come from the preconditions the move already captures, so this needs no extra lookup.
 * A `null` result means every destination is a genuine move for at least one item, which is why the
 * dialog must not disable Move against it.
 */
function resolveSourceFolderId(
  filePreconditions: readonly FileSelectionPrecondition[],
  folderPreconditions: readonly FolderSelectionPrecondition[]
): string | null {
  const parentIds = new Set<string | null>()
  filePreconditions.forEach((file) => parentIds.add(file.folder_id))
  folderPreconditions.forEach((folder) => parentIds.add(folder.parent_id))

  if (parentIds.size !== 1) return null

  const [only] = [...parentIds]
  return only
}

export function useMoveDialog({
  files,
  folders,
  currentFolderId,
  selectedSelectableFileIds,
  selectedFolderIds,
  ancestorFolderIdsById = kNoSelectionAncestry,
}: UseMoveDialogParams): UseMoveDialogReturn {
  const isAnyOperationActive = useAppSelector(selectIsUserFilesOperationActive)

  // Snapshot selection at open time so the confirmed move uses the rows the user selected.
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [fileIdsToMove, setFileIdsToMove] = useState<string[]>([])
  const [folderIdsToMove, setFolderIdsToMove] = useState<string[]>([])
  const [filePreconditions, setFilePreconditions] = useState<FileSelectionPrecondition[]>([])
  const [folderPreconditions, setFolderPreconditions] = useState<FolderSelectionPrecondition[]>([])
  const [sourceFolderId, setSourceFolderId] = useState<string | null>(null)
  const [selectedCounts, setSelectedCounts] = useState({ fileCount: 0, folderCount: 0 })
  const selectedFileIdSet = useMemo(() => new Set(selectedSelectableFileIds), [selectedSelectableFileIds])
  const selectedFolderIdSet = useMemo(() => new Set(selectedFolderIds), [selectedFolderIds])
  const moveSelectedDisabled = useMemo(
    () =>
      files.some((file) => selectedFileIdSet.has(file.id) && (!isLocalStorageItem(file) || isWaitingForOcrQuota(file))) ||
      folders.some((folder) => selectedFolderIdSet.has(folder.id) && !isLocalStorageItem(folder)),
    [files, folders, selectedFileIdSet, selectedFolderIdSet]
  )

  /**
   * `selectedCounts` is what the dialog titles itself from. It defaults to the ids being moved,
   * which is right for every caller that names them directly (the row menu, a drag). Only
   * `openMoveForSelected` passes it, because only there do the two differ.
   */
  const openMoveItems = useCallback(
    ({ fileIds = [], folderIds = [] }: OpenMoveItemsArgs, selectedCounts?: { fileCount: number; folderCount: number }) => {
      if (!currentFolderId) return
      if (fileIds.length === 0 && folderIds.length === 0) return
      setFileIdsToMove([...fileIds])
      setFolderIdsToMove([...folderIds])
      setSelectedCounts(selectedCounts ?? { fileCount: fileIds.length, folderCount: folderIds.length })
      // Sets rather than `Array.includes`: search results are matter-wide, so both the rows and the
      // selection can run to thousands and the nested scan would be quadratic.
      const fileIdSet = new Set(fileIds)
      const folderIdSet = new Set(folderIds)
      const nextFilePreconditions = files.filter((file) => fileIdSet.has(file.id)).map(buildFileSelectionPrecondition)
      const nextFolderPreconditions = folders.filter((folder) => folderIdSet.has(folder.id)).map(buildFolderSelectionPrecondition)
      setFilePreconditions(nextFilePreconditions)
      setFolderPreconditions(nextFolderPreconditions)
      setSourceFolderId(resolveSourceFolderId(nextFilePreconditions, nextFolderPreconditions))
      setMoveDialogOpen(true)
    },
    [currentFolderId, files, folders]
  )

  const openMoveForSelected = useCallback(() => {
    if (moveSelectedDisabled) return

    // Folders and files are batched into separate requests, so an item that travels inside a
    // moving folder must not also be moved on its own — it would be pulled back out.
    //
    // The pruned item is still one the user ticked, and it still lands in the destination inside its
    // folder, so the dialog keeps titling itself from the whole selection (FE-594 / BB-04).
    const pruned = pruneCoveredSelection({ fileIds: selectedSelectableFileIds, folderIds: selectedFolderIds, ancestorFolderIdsById })
    openMoveItems(pruned, { fileCount: selectedSelectableFileIds.length, folderCount: selectedFolderIds.length })
  }, [moveSelectedDisabled, openMoveItems, selectedSelectableFileIds, selectedFolderIds, ancestorFolderIdsById])

  const closeMoveDialog = useCallback(() => {
    setMoveDialogOpen(false)
    setFileIdsToMove([])
    setFolderIdsToMove([])
    setFilePreconditions([])
    setFolderPreconditions([])
    setSourceFolderId(null)
    setSelectedCounts({ fileCount: 0, folderCount: 0 })
  }, [])

  return {
    moveDialog: {
      open: moveDialogOpen,
      fileIds: fileIdsToMove,
      folderIds: folderIdsToMove,
      selectedFileCount: selectedCounts.fileCount,
      selectedFolderCount: selectedCounts.folderCount,
      filePreconditions,
      folderPreconditions,
      sourceFolderId,
    },
    openMoveForSelected,
    moveSelectedDisabled,
    openMoveItems,
    closeMoveDialog,
    isSubmitting: isAnyOperationActive,
  }
}
